import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { X, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const EditSavingsGoalDialog = ({ goal, open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    target_amount: 60,
    contribution_amount: 5,
    contribution_day: 1,
    mode: 'automatic'
  });

  useEffect(() => {
    if (goal && open) {
      setFormData({
        name: goal.name || '',
        target_amount: goal.target_amount || 60,
        contribution_amount: goal.contribution_amount || 5,
        contribution_day: goal.contribution_day || 1,
        mode: goal.mode || 'automatic'
      });
    }
  }, [goal, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/savings-goals/${goal.id}`, formData, { withCredentials: true });
      toast.success('Objectif mis à jour ! 👍');
      onSaved();
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/savings-goals/${goal.id}/regenerate`, {}, { withCredentials: true });
      toast.success(`${response.data.count} versements générés dans le calendrier ! 📅`);
      onSaved();
    } catch (error) {
      console.error('Error regenerating operations:', error);
      toast.error('Erreur lors de la génération des versements');
    } finally {
      setRegenerating(false);
    }
  };

  if (!open) return null;

  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
          <h2 className="text-lg font-bold">Modifier l'objectif</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {/* Name */}
          <div>
            <Label htmlFor="name">Nom de l'objectif</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ex: Anniversaire, Vacances..."
              className="mt-1"
            />
          </div>

          {/* Target Amount */}
          <div>
            <Label htmlFor="target_amount">Montant cible (€)</Label>
            <Input
              id="target_amount"
              type="number"
              min="1"
              step="1"
              value={formData.target_amount}
              onChange={(e) => handleChange('target_amount', parseFloat(e.target.value))}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Montant total que tu souhaites atteindre</p>
          </div>

          {/* Mode Toggle */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="auto-mode" className="cursor-pointer">Versements automatiques</Label>
              <Switch
                id="auto-mode"
                checked={formData.mode === 'automatic'}
                onCheckedChange={(checked) => handleChange('mode', checked ? 'automatic' : 'manual')}
              />
            </div>
            <p className="text-xs text-gray-600">
              {formData.mode === 'automatic' 
                ? '✅ Les versements seront créés automatiquement dans ton calendrier'
                : '📝 Tu devras ajouter les versements manuellement'
              }
            </p>
          </div>

          {/* Automatic mode options */}
          {formData.mode === 'automatic' && (
            <div className="space-y-4 border-l-4 border-teal-500 pl-4 bg-teal-50/50 py-3 rounded-r-lg">
              {/* Contribution Amount */}
              <div>
                <Label htmlFor="contribution_amount">Montant mensuel (€)</Label>
                <Input
                  id="contribution_amount"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.contribution_amount}
                  onChange={(e) => handleChange('contribution_amount', parseFloat(e.target.value))}
                  className="mt-1 bg-white"
                />
              </div>

              {/* Contribution Day */}
              <div>
                <Label htmlFor="contribution_day">Jour du prélèvement</Label>
                <Select
                  value={formData.contribution_day.toString()}
                  onValueChange={(value) => handleChange('contribution_day', parseInt(value))}
                >
                  <SelectTrigger className="mt-1 bg-white">
                    <SelectValue placeholder="Choisir le jour" />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOptions.map(day => (
                      <SelectItem key={day} value={day.toString()}>
                        Le {day} du mois
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-600 mt-1">
                  💡 Choisis une date après ta paie pour plus de confort
                </p>
              </div>

              {/* Regenerate button */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-teal-300 text-teal-700 hover:bg-teal-50"
                onClick={handleRegenerate}
                disabled={regenerating}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Génération...' : 'Générer les versements dans le calendrier'}
              </Button>
            </div>
          )}

          {/* Estimated time */}
          {formData.mode === 'automatic' && formData.contribution_amount > 0 && (
            <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
              <p className="text-sm text-cyan-800">
                📊 Avec {formData.contribution_amount}€/mois, tu atteindras {formData.target_amount}€ en environ{' '}
                <strong>{Math.ceil(formData.target_amount / formData.contribution_amount)} mois</strong>
              </p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
            disabled={loading}
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditSavingsGoalDialog;
